import {
  BookOpenIcon,
  ChartPieIcon,
  GiftIcon,
  HeartIcon,
  InboxIcon,
  MapPinIcon,
  PaperAirplaneIcon,
  StarIcon,
  SwatchIcon,
  WifiIcon,
  InboxArrowDownIcon as MailboxIcon,
  BuildingOffice2Icon,
  HomeIcon,
  GlobeEuropeAfricaIcon,
  QrCodeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/16/solid'
import { TrendingUpDown } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import { useLocalStorage } from 'usehooks-ts'
import { type UserRoleType, AvailableRoles } from '@/state/auth/auth.types'
import { useBreakpointValue } from '@/hooks/use-color-mode-standalone'
import useUser, { useUserRole } from '@/hooks/useUser'
import { useIsMobile } from '@/hooks/use-mobile'
import { useSelectedVenue } from '@/state/backend/venues/venues.hooks'
import useSelectedOrganisation from '@/state/entities/organisations/hooks/useSelectedOrganisation'
import inboxApi from '@/state/inbox/inbox.slice'
import wifi from '@/state/wifi/wifi.slice'
import zionApi from '@/state/zion/zion.slice'
import tankApi from '@/state/tank/tank.slice'
import { getFirst } from '@/utils/common'
import { type ActionBarItemProps, type MainMenuKeyType } from './types'

export const useMenu = ({
  toInclude = [
    'admin',
    'home',
    'venues',
    'wi-fi',
    'bookings',
    'reviews',
    'marketing',
    'inbox',
    'loyalty',
    'gift-cards',
    'reports',
    'brand',
  ],
}: {
  toInclude?: Partial<MainMenuKeyType>[]
}) => {
  // Threshold hook, not a live width — see useUserConfig: per-resize width
  // state in shell-level hooks re-renders the whole app during window drags.
  const isMobile = useIsMobile()

  // const size = useBreakpointValue({ base: 'md', lg: 'md' })
  const organisation = useSelectedOrganisation()
  const role = useUserRole()

  const { serial } = useParams<{ serial: string }>()

  const { data: selectedVenue } = useSelectedVenue(serial)
  const { data: unreadCount = { open: 0, closed: 0 } } =
    inboxApi.useGetUnreadCountQuery(
      {
        orgId: organisation?.id!,
        serial,
      },
      {
        skip: !organisation?.id || !serial || serial === 'unlinked',
        refetchOnFocus: true,
      }
    )
  const toIncludeWithAuth = useMemo(() => {
    if (!role) return []

    const denyByKey: Record<UserRoleType['key'], Partial<MainMenuKeyType>[]> = {
      super: [],
      reseller: ['admin'],
      admin: ['admin'],
      moderator: ['gift-cards', 'admin'],
      gm: ['marketing', 'wi-fi', 'reports', 'gift-cards', 'brand', 'admin'],
      reports: [
        'marketing',
        'gift-cards',
        'inbox',
        'venues',
        'reviews',
        'loyalty',
        'brand',
        'admin',
      ],
      marketeer: ['bookings', 'inbox', 'gift-cards', 'admin'],
      booking: [
        'marketing',
        'wi-fi',
        'reports',
        'gift-cards',
        'brand',
        'admin',
      ],
      'front-of-house': [
        'admin',
        'gift-cards',
        'venues',
        'reports',
        'wi-fi',
        'marketing',
        'brand',
        'loyalty',
        'reviews',
        'inbox',
      ],
      'it-support': [
        'admin',
        'gift-cards',
        'bookings',
        'loyalty',
        'brand',
        'reviews',
        'inbox',
        'marketing',
        'reports',
        'venues',
      ],
    }

    // Fallback using AvailableRoles name mapping when key is not sufficient
    const denyByName: Partial<
      Record<AvailableRoles, Partial<MainMenuKeyType>[]>
    > = {
      [AvailableRoles.SuperAdmin]: denyByKey.super,
      [AvailableRoles.Reseller]: denyByKey.reseller,
      [AvailableRoles.Admin]: denyByKey.admin,
      [AvailableRoles.Moderator]: denyByKey.moderator,
      [AvailableRoles.GM]: denyByKey.gm,
      [AvailableRoles.Reports]: denyByKey.reports,
      [AvailableRoles.Marketeer]: denyByKey.marketeer,
      [AvailableRoles.Booking]: denyByKey.booking,
      [AvailableRoles.FrontOfHouse]: denyByKey['front-of-house'],
      [AvailableRoles.ITSupport]: denyByKey['it-support'],
    }

    const denied =
      denyByKey[role.key] ?? (role.name ? denyByName[role.name] : []) ?? []
    return toInclude.filter((item: any) => !(denied as string[]).includes(item))
  }, [toInclude, role])

  const [shadowVenue, setShadowVenue] = useLocalStorage(
    `active-menu-venue`,
    selectedVenue
  )

  const MAX_MOBILE_ITEMS = shadowVenue ? 3 : 4

  useEffect(() => {
    if (!selectedVenue) return
    setShadowVenue(selectedVenue)
  }, [selectedVenue])

  const { data: wifiConfigs = [] } = wifi.useGetConfigsQuery(
    {
      orgId: organisation?.id ?? '',
    },
    {
      skip: !organisation?.id,
    }
  )

  const { data: enquiryCounts } = zionApi.useGetEnquiryCountsQuery(
    {
      org_id: organisation?.id ?? '',
    },
    {
      skip: !organisation?.id,
    }
  )

  const { data: venueEnquiryCounts } = zionApi.useGetEnquiryCountsQuery(
    {
      org_id: organisation?.id ?? '',
      serial: serial ?? shadowVenue?.serial,
    },
    {
      skip: !organisation?.id || (!serial && !shadowVenue?.serial),
    }
  )

  const pendingEnquiries = enquiryCounts?.counts?.by_status?.pending ?? 0
  const venuePendingEnquiries =
    venueEnquiryCounts?.counts?.by_status?.pending ?? 0

  const { data: pendingRepliesData } = tankApi.useGetPendingRepliesQuery(
    {
      orgId: organisation?.id ?? '',
      query: {
        status: 'PENDING',
        limit: 100,
        offset: 0,
      },
    },
    { skip: !organisation?.id }
  )

  const pendingRepliesCount = pendingRepliesData?.data?.length ?? 0
  const user = useUser()
  const menuItems = useMemo((): {
    visible: ActionBarItemProps[]
    overflow: ActionBarItemProps[]
    venue: ActionBarItemProps[]
  } => {
    if (!toIncludeWithAuth) return { visible: [], overflow: [], venue: [] }
    const locations = organisation?.locations ?? []
    const firstLocation = getFirst(locations)
    const items = toIncludeWithAuth
      .map((item): ActionBarItemProps | null => {
        switch (item) {
          case 'admin':
            return {
              name: 'Admin',
              icon: BuildingOffice2Icon,
              id: 'admin',
              to: `/admin`,
              className: '',
            }
          // case 'home':
          //   return {
          //     name: 'Home',
          //     icon: GlobeEuropeAfricaIcon,
          //     id: 'home',
          //     to: ``,
          //     className: '',
          //   }
          case 'venues':
            return locations.length > 1
              ? {
                  name: 'Venues',
                  icon: HomeIcon,
                  id: 'venues',
                  to: `/${organisation?.id ?? ''}/venues`,
                  className: '',
                }
              : {
                  name:
                    firstLocation?.alias ?? firstLocation?.serial ?? 'Venue',
                  icon: HomeIcon,
                  id: 'venues',
                  to: `/${organisation?.id ?? ''}/venues/${firstLocation?.serial ?? ''}`,
                  className: '',
                }

          case 'inbox':
            return locations.length === 1
              ? {
                  name: 'Inbox',
                  icon: MailboxIcon,
                  id: 'inbox',
                  to: `/${organisation?.id ?? ''}/venues/${firstLocation?.serial ?? ''}/inbox`,
                  count: unreadCount.open,
                  className: '',
                }
              : {
                  name: 'Inbox',
                  icon: MailboxIcon,
                  id: 'inbox',
                  to: `/${organisation?.id ?? ''}/inbox`,
                  count: unreadCount.open,
                  className: '',
                }

          case 'marketing':
            return {
              name: 'Marketing',
              icon: PaperAirplaneIcon,
              id: 'marketing',
              to: `/${organisation?.id ?? ''}/marketing`,
              className: '',
            }

          case 'reviews':
            return {
              name: 'Reviews',
              icon: StarIcon,
              id: 'reviews',
              to: `/${organisation?.id ?? ''}/reviews`,
              count: pendingRepliesCount > 0 ? pendingRepliesCount : undefined,
              className: '',
            }

          case 'loyalty':
            return {
              name: 'Loyalty',
              icon: HeartIcon,
              id: 'loyalty',
              to: `/${organisation?.id ?? ''}/loyalty`,
              className: '',
            }

          case 'bookings':
            return locations.length === 1 && firstLocation
              ? {
                  name: 'Bookings',
                  icon: BookOpenIcon,
                  id: 'bookings',
                  to: `/${organisation?.id ?? ''}/venues/${firstLocation?.serial ?? ''}/bookings`,
                  count:
                    venuePendingEnquiries > 0
                      ? venuePendingEnquiries
                      : undefined,
                  className: '',
                }
              : {
                  name: 'Bookings',
                  icon: BookOpenIcon,
                  id: 'bookings',
                  to: `/${organisation?.id ?? ''}/bookings`,
                  count: pendingEnquiries > 0 ? pendingEnquiries : undefined,
                  className: '',
                }
          case 'wi-fi':
            return locations.length === 1 && firstLocation
              ? {
                  name: 'Wi-Fi',
                  icon: WifiIcon,
                  id: 'wi-fi',
                  to: `/${organisation?.id ?? ''}/venues/${firstLocation?.serial ?? ''}/wi-fi`,
                  className: '',
                }
              : {
                  name: 'Wi-Fi',
                  icon: WifiIcon,
                  id: 'wi-fi',
                  to: `/${organisation?.id ?? ''}/wi-fi`,
                  count: wifiConfigs.filter(
                    (item: any) => !item?.inform?.status
                  ).length,
                  className: '',
                }
          case 'gift-cards':
            return {
              name: 'Gifting',
              icon: GiftIcon,
              id: 'gift-cards',
              to: `/${organisation?.id ?? ''}/gifting`,
              className: '',
            }
          case 'reports':
            return {
              name: 'Reports',
              icon: TrendingUpDown,
              id: 'reports',
              to: `/${organisation?.id ?? ''}/reports`,
              className: '',
            }
          case 'scanner':
            return {
              name: 'QR Scanner',
              icon: QrCodeIcon,
              id: 'scanner',
              to: `/${organisation?.id ?? ''}/scanner`,
              className: '',
            }
          case 'chat':
            return {
              name: 'AI Chat',
              icon: ChatBubbleLeftRightIcon,
              id: 'chat',
              to: `/${organisation?.id ?? ''}/chat`,
              className: '',
            }
          case 'brand':
            return locations.length === 1 && firstLocation
              ? {
                  name: 'Brand',
                  icon: SwatchIcon,
                  id: 'brand',
                  to: `/${organisation?.id ?? ''}/venues/${firstLocation?.serial ?? ''}/brand`,
                  className: '',
                }
              : {
                  name: 'Brand',
                  icon: SwatchIcon,
                  id: 'brand',
                  to: `/${organisation?.id ?? ''}/account/organisation/brand`,
                  className: '',
                }
        }
        return null
      })
      .filter((i): i is ActionBarItemProps => Boolean(i))

    const venueMenu = toIncludeWithAuth
      .map((item): ActionBarItemProps | null => {
        switch (item) {
          case 'admin':
            return {
              name: 'Admin',
              icon: BuildingOffice2Icon,
              id: 'admin',
              to: `/admin`,
              className: '',
            }
          case 'home':
            return {
              name: 'Home',
              icon: HomeIcon,
              id: 'home',
              to: ``,
              className: '',
            }
          case 'venues':
            return {
              name: shadowVenue?.alias ?? shadowVenue?.serial ?? 'Venue',
              icon: MapPinIcon,
              id: '',
              to: `/${organisation?.id ?? ''}/venues/${shadowVenue?.serial ?? ''}`,
              className: '',
            }
          case 'wi-fi':
            return {
              name: 'Wi-Fi',
              icon: WifiIcon,
              id: 'wi-fi',
              to: `/${organisation?.id ?? ''}/venues/${shadowVenue?.serial ?? ''}/wi-fi`,
              className: '',
            }
          case 'brand':
            return {
              name: 'Brand',
              icon: SwatchIcon,
              id: 'brand',
              to: `/${organisation?.id ?? ''}/venues/${shadowVenue?.serial ?? ''}/brand`,
              className: '',
            }
          case 'bookings':
            return {
              name: 'Bookings',
              icon: BookOpenIcon,
              id: 'bookings',
              to: `/${organisation?.id ?? ''}/venues/${shadowVenue?.serial ?? ''}/bookings`,
              count:
                venuePendingEnquiries > 0 ? venuePendingEnquiries : undefined,
              className: '',
            }
          case 'inbox':
            return {
              name: 'Inbox',
              icon: InboxIcon,
              id: 'inbox',
              to: `/${organisation?.id ?? ''}/venues/${shadowVenue?.serial ?? ''}/inbox`,
              count: unreadCount.open,
              className: '',
            }
          case 'reviews':
            return {
              name: 'Reviews',
              icon: StarIcon,
              id: 'reviews',
              to: `/${organisation?.id ?? ''}/venues/${shadowVenue?.serial ?? ''}/reviews`,
              count: pendingRepliesCount > 0 ? pendingRepliesCount : undefined,
              className: '',
            }
          case 'loyalty':
            return {
              name: 'Loyalty',
              icon: HeartIcon,
              id: 'loyalty',
              to: `/${organisation?.id ?? ''}/venues/${shadowVenue?.serial ?? ''}/loyalty`,
              className: '',
            }
          case 'reports':
            return {
              name: 'Venue Snapshot',
              icon: ChartPieIcon,
              id: 'reports',
              to: `/${organisation?.id ?? ''}/venues/${shadowVenue?.serial ?? ''}/reports/monthly-snapshot`,
              className: '',
            }

          default:
            return null
        }
      })
      .filter((i): i is ActionBarItemProps => Boolean(i))

    if (isMobile) {
      return {
        visible: items,
        overflow: [] as ActionBarItemProps[],
        venue: selectedVenue ? venueMenu : [],
      }
    }

    return {
      visible: items,
      overflow: [] as ActionBarItemProps[],
      venue: shadowVenue ? venueMenu : [],
    }
  }, [
    toIncludeWithAuth,
    organisation?.locations,
    organisation?.id,
    isMobile,
    unreadCount.open,
    shadowVenue,
    user,
    pendingEnquiries,
    venuePendingEnquiries,
    pendingRepliesCount,
  ])

  return menuItems
}
